import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  Card,
  DatePicker,
  Empty,
  Form,
  InputNumber,
  Modal,
  Segmented,
  Select,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from "antd";
import {
  CreditCardOutlined,
  DollarOutlined,
  GiftOutlined,
  PlusOutlined,
  ThunderboltOutlined,
  WalletOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import apiClient from "../../actions/api";
import openNotification from "../../components/Notifications/openNotification";
import { updateBalance } from "../../actions/auth";
import "../../style/user/billing.scss";
import btcIcon from "../../assets/crypto/btc.svg";
import ethIcon from "../../assets/crypto/eth.svg";
import usdtIcon from "../../assets/crypto/usdt.svg";
import ltcIcon from "../../assets/crypto/ltc.svg";
import solIcon from "../../assets/crypto/sol.svg";
import trxIcon from "../../assets/crypto/trx.svg";
import bnbIcon from "../../assets/crypto/bnb.svg";
import usdcIcon from "../../assets/crypto/usdc.svg";

const { RangePicker } = DatePicker;
const coinLabel = (icon, symbol, name, network) => (
  <span className="coin-option">
    <img src={icon} alt="" />
    <span>
      <strong>{symbol}</strong>
      <small>
        {name}
        {network ? ` · ${network}` : ""}
      </small>
    </span>
  </span>
);
const currencyOptions = [
  { value: "btc", label: coinLabel(btcIcon, "BTC", "Bitcoin") },
  { value: "eth", label: coinLabel(ethIcon, "ETH", "Ethereum") },
  { value: "trx", label: coinLabel(trxIcon, "TRX", "Tron") },
  { value: "usdtbsc", label: coinLabel(usdtIcon, "USDT", "Tether", "BEP-20") },
  {
    value: "usdttrc20",
    label: coinLabel(usdtIcon, "USDT", "Tether", "TRC-20"),
  },
  {
    value: "usdterc20",
    label: coinLabel(usdtIcon, "USDT", "Tether", "ERC-20"),
  },
  { value: "bnbbsc", label: coinLabel(bnbIcon, "BNB", "BNB", "BEP-20") },
  { value: "usdc", label: coinLabel(usdcIcon, "USDC", "USD Coin", "ERC-20") },
  {
    value: "usdcbsc",
    label: coinLabel(usdcIcon, "USDC", "USD Coin", "BEP-20"),
  },
  {
    value: "usdcmatic",
    label: coinLabel(usdcIcon, "USDC", "USD Coin", "Polygon"),
  },
  { value: "ltc", label: coinLabel(ltcIcon, "LTC", "Litecoin") },
  { value: "sol", label: coinLabel(solIcon, "SOL", "Solana") },
];
const coinIcons = {
  btc: btcIcon,
  eth: ethIcon,
  trx: trxIcon,
  usdtbsc: usdtIcon,
  usdttrc20: usdtIcon,
  usdterc20: usdtIcon,
  bnbbsc: bnbIcon,
  usdc: usdcIcon,
  usdcbsc: usdcIcon,
  usdcmatic: usdcIcon,
  ltc: ltcIcon,
  sol: solIcon,
};
const categoryLabels = {
  top_up: "Balance top-up",
  answer: "Application answer",
  resume: "Tailored resume",
  cover_letter: "Cover letter",
  job_url: "Job Board URL",
  adjustment: "Balance adjustment",
};
const paymentStatusContent = {
  pending: {
    title: "Payment awaiting confirmation",
    description: "Send the exact amount using the selected network.",
  },
  completed: {
    title: "Payment completed",
    description: "This payment has been confirmed and added to your balance.",
  },
  failed: {
    title: "Payment failed",
    description: "This payment could not be completed.",
  },
  cancelled: {
    title: "Payment cancelled",
    description: "This payment is no longer active.",
  },
};

const Billing = ({ title }) => {
  document.title = title;
  const balance = useSelector((state) => state.auth.balance);
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [range, setRange] = useState([dayjs().subtract(30, "day"), dayjs()]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [payment, setPayment] = useState(null);
  const [transactionFilter, setTransactionFilter] = useState("all");

  const filteredTransactions = useMemo(() => {
    if (transactionFilter === "balance") {
      return transactions.filter(({ category }) =>
        ["top_up", "adjustment"].includes(category),
      );
    }

    if (transactionFilter === "other") {
      return transactions.filter(
        ({ category }) => !["top_up", "adjustment"].includes(category),
      );
    }

    return transactions;
  }, [transactionFilter, transactions]);

  const loadHistory = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/billing", {
        params: {
          startDate: range?.[0]?.format("YYYY-MM-DD"),
          endDate: range?.[1]?.format("YYYY-MM-DD"),
        },
      });
      setTransactions(data.transactions || []);
      dispatch(updateBalance(data.balance));
    } catch (error) {
      openNotification(
        "error",
        "Billing unavailable",
        error?.response?.data?.errors?.[0]?.msg ||
          "Unable to load transactions.",
      );
    } finally {
      setLoading(false);
    }
  }, [dispatch, range]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);
  useEffect(() => {
    if (!transactions.some((item) => item.status === "pending"))
      return undefined;
    const interval = window.setInterval(loadHistory, 15000);
    return () => window.clearInterval(interval);
  }, [loadHistory, transactions]);

  const createTopUp = async (values) => {
    setCreating(true);
    try {
      const { data } = await apiClient.post("/billing/top-up", values);
      setPayment({
        ...data.payment,
        status: data.transaction?.status || "pending",
      });
      message.success("Crypto payment created");
      await loadHistory();
    } catch (error) {
      openNotification(
        "error",
        "Payment could not be created",
        error?.response?.data?.errors?.[0]?.msg || "Please try again.",
      );
    } finally {
      setCreating(false);
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    setPayment(null);
    form.resetFields();
  };
  const showPayment = (transaction) => {
    if (!transaction.paymentAddress || !transaction.paymentAmount) return;

    setPayment({
      paymentId: transaction.providerId,
      status: transaction.status,
      address: transaction.paymentAddress,
      amount: transaction.paymentAmount,
      currency: transaction.currency,
    });
    setModalOpen(true);
  };
  const columns = [
    {
      title: "Transaction",
      dataIndex: "category",
      render: (value, record) => (
        <div className="billing-transaction-name">
          <span className={record.type === "credit" ? "credit" : "debit"}>
            {record.type === "credit" ? "+" : "−"}
          </span>
          <div>
            <strong>{categoryLabels[value] || value}</strong>
            <small>{record.reference || "Usage charge"}</small>
          </div>
        </div>
      ),
    },
    {
      title: "Date",
      dataIndex: "createdAt",
      render: (value) => dayjs(value).format("DD MMM YYYY, HH:mm"),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (value) => (
        <Tag
          color={
            value === "completed"
              ? "green"
              : value === "pending"
                ? "gold"
                : "red"
          }
        >
          {value}
        </Tag>
      ),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      align: "right",
      render: (value, record) => (
        <strong className={`billing-amount ${record.type}`}>
          {record.type === "credit" ? "+" : "−"}${Number(value).toFixed(3)}
        </strong>
      ),
    },
  ];

  return (
    <div className="billing-page">
      <header className="billing-heading">
        <div>
          <span className="billing-heading-icon">
            <WalletOutlined />
          </span>
          <div>
            <h1>Billing</h1>
            <p>Manage your balance and review account activity.</p>
          </div>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setModalOpen(true)}
        >
          Top up balance
        </Button>
      </header>
      <Card className="billing-balance-card" variant="borderless">
        <div>
          <span>Available balance</span>
          <strong>${Number(balance || 0).toFixed(3)}</strong>
          <p>Used automatically for AI answers and tailored documents.</p>
        </div>
        <div className="billing-balance-art">
          <DollarOutlined />
        </div>
      </Card>
      <section className="billing-history">
        <div className="billing-history-head">
          <div>
            <h2>Transaction history</h2>
            <p>Your top-ups, usage charges, and balance adjustments.</p>
          </div>
          <RangePicker
            value={range}
            onChange={(value) => setRange(value || [])}
            allowClear
          />
        </div>
        <div
          className="billing-history-filter"
          aria-label="Filter transactions"
        >
          <Segmented
            value={transactionFilter}
            onChange={setTransactionFilter}
            options={[
              { label: "All transactions", value: "all" },
              { label: "Balance", value: "balance" },
              { label: "Other transactions", value: "other" },
            ]}
          />
        </div>
        <Table
          rowKey="_id"
          columns={columns}
          dataSource={filteredTransactions}
          loading={loading}
          pagination={{ pageSize: 10, showSizeChanger: false }}
          onRow={(record) => ({
            onClick: () => showPayment(record),
            className:
              record.paymentAddress && record.paymentAmount
                ? "billing-payment-row"
                : "",
          })}
          locale={{
            emptyText: (
              <Empty description="No matching transactions in this date range" />
            ),
          }}
          scroll={{ x: 680 }}
        />
      </section>

      <Modal
        className="topup-modal"
        title={
          <span>
            <WalletOutlined /> Top up your balance
          </span>
        }
        open={modalOpen}
        onCancel={closeModal}
        footer={null}
        width={580}
        destroyOnHidden
      >
        {!payment ? (
          <>
            <Tabs
              className="topup-method-tabs"
              defaultActiveKey="crypto"
              items={[
                {
                  key: "card",
                  disabled: true,
                  label: (
                    <span>
                      <CreditCardOutlined /> Credit Card
                    </span>
                  ),
                },
                {
                  key: "crypto",
                  label: (
                    <span>
                      <ThunderboltOutlined /> Crypto
                    </span>
                  ),
                },
                {
                  key: "voucher",
                  disabled: true,
                  label: (
                    <span>
                      <GiftOutlined /> Redeem Voucher
                    </span>
                  ),
                },
              ]}
            />
            <Form
              form={form}
              layout="vertical"
              initialValues={{ amount: 10, currency: "btc" }}
              onFinish={createTopUp}
            >
              <Form.Item
                label="Amount"
                name="amount"
                rules={[{ required: true, type: "number", min: 1 }]}
              >
                <InputNumber
                  min={1}
                  precision={2}
                  prefix="$"
                  placeholder="10.00"
                  style={{ width: "100%" }}
                />
              </Form.Item>
              <Form.Item
                label="Crypto Currency"
                name="currency"
                rules={[{ required: true }]}
              >
                <Select
                  popupClassName="crypto-currency-dropdown"
                  optionLabelProp="label"
                  options={currencyOptions}
                />
              </Form.Item>
              <div className="topup-provider-note">
                <ThunderboltOutlined />
                <span>
                  Secure crypto payment powered by <strong>NOWPayments</strong>.
                  Your balance updates automatically after confirmation.
                </span>
              </div>
              <Button
                block
                type="primary"
                htmlType="submit"
                size="large"
                loading={creating}
              >
                Continue to payment
              </Button>
            </Form>
          </>
        ) : (
          <div className="crypto-payment-details">
            <div
              className={`crypto-payment-status ${payment.status || "pending"}`}
            >
              <img
                src={
                  coinIcons[String(payment.currency || "").toLowerCase()] ||
                  usdtIcon
                }
                alt=""
              />
              <div>
                <strong>
                  {
                    (
                      paymentStatusContent[payment.status] ||
                      paymentStatusContent.pending
                    ).title
                  }
                </strong>
                <span>
                  {
                    (
                      paymentStatusContent[payment.status] ||
                      paymentStatusContent.pending
                    ).description
                  }
                </span>
                <Tag
                  color={
                    payment.status === "completed"
                      ? "green"
                      : payment.status === "pending"
                        ? "gold"
                        : "red"
                  }
                >
                  {payment.status || "pending"}
                </Tag>
              </div>
            </div>
            <Typography.Text type="secondary">Amount to pay</Typography.Text>
            <Typography.Paragraph copyable strong>
              {payment.amount} {String(payment.currency || "").toUpperCase()}
            </Typography.Paragraph>
            <Typography.Text type="secondary">Payment address</Typography.Text>
            <Typography.Paragraph copyable className="payment-address">
              {payment.address}
            </Typography.Paragraph>
            <Button block onClick={closeModal}>
              Done
            </Button>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Billing;
